import React, { useState, useEffect } from 'react';
import { Stack, TextField, FormControlLabel, Switch } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '../../../../../app/hooks';
import { useUpsertRuleMutation, useDeleteRuleMutation, contractApi } from '../../../../../services/ContractService';
import { RuleCommands } from './RuleCommands';

interface ZeroRateOverrideProps {
  ruleId: string;
  ruleString?: string;
  ruleDescription?: string;
}

interface ZeroRateOverrideRule {
  percentOfBilledCharges: number;
  includeMissingRates: boolean;
}

const ZeroRateOverride: React.FC<ZeroRateOverrideProps> = ({
  ruleId,
  ruleString,
  ruleDescription,
}) => {
  const [searchParams] = useSearchParams();
  const contractId = searchParams.get('id');
  const dispatch = useAppDispatch();
  const [rule, setRule] = useState<ZeroRateOverrideRule>({
    percentOfBilledCharges: 100,
    includeMissingRates: false,
  });

  const [upsertRule] = useUpsertRuleMutation();
  const [deleteRule] = useDeleteRuleMutation();

  useEffect(() => {
    if (ruleString && ruleString !== '') {
      try {
        const parsedRule: ZeroRateOverrideRule = JSON.parse(ruleString);
        setRule(parsedRule);
      } catch (error) {
        console.error('Failed to parse rule string:', error);
      }
    }
  }, [ruleString, ruleId]);

  const save = async () => {
    if (!contractId) return;

    try {
      const ruleData = {
        contractId: contractId,
        ruleId: ruleId === '0' ? '' : ruleId,
        ruleType: 'ZeroRateOverride',
        priority: 0,
        ruleString: JSON.stringify(rule),
        description: ruleDescription ?? '',
      };

      await upsertRule(ruleData).unwrap();
      dispatch(contractApi.util.invalidateTags(['RuleList']));
    } catch (error) {
      console.error('Failed to save rule:', error);
    }
  };

  const ruleDelete = async () => {
    if (ruleId === '0') return;

    try {
      await deleteRule({ ruleId }).unwrap();
      dispatch(contractApi.util.invalidateTags(['RuleList']));
    } catch (error) {
      console.error('Failed to delete rule:', error);
    }
  };

  return (
    <React.Fragment>
      <Stack spacing={2} sx={{ mb: 2 }}>
        <TextField
          type="number"
          label="Percent of Billed Charges"
          size="small"
          value={rule.percentOfBilledCharges}
          onChange={(e) =>
            setRule({ ...rule, percentOfBilledCharges: Number(e.target.value) })
          }
          sx={{ width: '220px' }}
          inputProps={{ step: 0.01, min: 0 }}
          InputLabelProps={{ shrink: true }}
          helperText="The percentage of billed charges to reimburse when the rate is $0"
        />
        <FormControlLabel
          control={
            <Switch
              checked={rule.includeMissingRates}
              onChange={(e) =>
                setRule({ ...rule, includeMissingRates: e.target.checked })
              }
            />
          }
          label="Include Missing Rates"
        />
      </Stack>
      <RuleCommands
        saveCommand={save}
        deleteCommand={ruleDelete}
        isNewRule={ruleId === '0'}
      />
    </React.Fragment>
  );
};

export { ZeroRateOverride };
